import React, { useRef, useEffect, useState } from 'react';
import { useClientsAndSubscriptionsQuery } from '../../services/hooks/useClienteSuscripciones';
import { Client, Pago, Suscripcion } from '../../types/index';
import { Printer, X } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { formatDate } from '../../utils/utils';
import Button from '../common/Button';

interface OtherPaymentsPrintListProps {
    onClose: () => void;
}

const OtherPaymentsPrintList: React.FC<OtherPaymentsPrintListProps> = ({ onClose }) => {
    const printRef = useRef<HTMLDivElement>(null);
    const [reportTitle, setReportTitle] = useState('Listado de Clientes - Otros Pagos');
    const [reportDate, setReportDate] = useState(new Date());
    const [filteredClients, setFilteredClients] = useState<Client[]>([]);
    const [showClosedPayments, setShowClosedPayments] = useState(true);
    const [showOpenPayments, setShowOpenPayments] = useState(true);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    // Usar el hook para obtener clientes y suscripciones
    const { data: clients, isLoading } = useClientsAndSubscriptionsQuery(true);

    // Filtrar clientes con suscripciones de tipo "pilates-otros-pagos"
    useEffect(() => {
        if (clients) {
            const filtered = clients.filter(client => 
                client.suscripciones && 
                client.suscripciones.some(sus => sus.nombreSuscripcion === "pilates-otros-pagos")
            ).map(client => ({
                ...client,
                suscripciones: client.suscripciones.filter(sus => sus.nombreSuscripcion === "pilates-otros-pagos")
            }));
            setFilteredClients(filtered);
        }
    }, [clients]);

    // Función para manejar la impresión
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: 'Listado_Clientes_Otros_Pagos',
        onAfterPrint: () => console.log('Impresión completada')
    });

    // Filtrar pagos según fechas y estado
    const filterPayments = (pagos: Pago[]) => {
        return pagos.filter(pago => {
            // Filtrar por estado
            if (!showClosedPayments && pago.estado === 'Cerrado') return false;
            if (!showOpenPayments && pago.estado === 'Abierto') return false;

            // Filtrar por rango de fechas (solo para pagos cerrados)
            if (pago.estado === 'Cerrado' && pago.fechaPago) {
                const pagoDate = new Date(pago.fechaPago);
                if (startDate && pagoDate < startDate) return false;
                if (endDate) {
                    const endOfDay = new Date(endDate);
                    endOfDay.setHours(23, 59, 59, 999);
                    if (pagoDate > endOfDay) return false;
                }
            }

            return true;
        });
    };

    // Formatear un número con separadores de miles y decimales
    const formatNumber = (num: number): string => {
        const parts = num.toFixed(2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return parts.join(',');
    };

    // Calcular el total de importes de pagos
    const calculateTotals = () => {
        let totalAbierto = 0;
        let totalCerrado = 0;

        filteredClients.forEach(client => {
            client.suscripciones.forEach(suscripcion => {
                const pagos = filterPayments(suscripcion.pagos || []);
                pagos.forEach(pago => {
                    if (pago.estado === 'Abierto') {
                        totalAbierto += pago.importe;
                    } else if (pago.estado === 'Cerrado') {
                        totalCerrado += pago.importe;
                    }
                });
            });
        });

        return { totalAbierto, totalCerrado };
    };

    const totals = calculateTotals();

    if (isLoading) {
        return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg">
                <p>Cargando datos...</p>
            </div>
        </div>;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 h-5/6 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">Vista Previa de Impresión</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Título del Reporte
                        </label>
                        <input
                            type="text"
                            value={reportTitle}
                            onChange={(e) => setReportTitle(e.target.value)}
                            className="w-full p-2 border rounded"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fecha Inicio
                            </label>
                            <input
                                type="date"
                                value={startDate ? startDate.toISOString().split('T')[0] : ''}
                                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                                className="w-full p-2 border rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fecha Fin
                            </label>
                            <input
                                type="date"
                                value={endDate ? endDate.toISOString().split('T')[0] : ''}
                                onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                                className="w-full p-2 border rounded"
                            />
                        </div>
                    </div>
                </div>

                <div className="mb-4 flex space-x-4">
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            checked={showOpenPayments}
                            onChange={() => setShowOpenPayments(!showOpenPayments)}
                            className="form-checkbox h-5 w-5 text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Mostrar Pagos Abiertos</span>
                    </label>
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            checked={showClosedPayments}
                            onChange={() => setShowClosedPayments(!showClosedPayments)}
                            className="form-checkbox h-5 w-5 text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Mostrar Pagos Cerrados</span>
                    </label>
                </div>

                <div className="flex-grow overflow-auto border rounded">
                    <div ref={printRef} className="p-8 bg-white">
                        {/* Cabecera para impresión */}
                        <div className="text-center mb-6">
                            <h1 className="text-2xl font-bold">{reportTitle}</h1>
                            <p className="text-gray-600">Fecha del reporte: {reportDate.toLocaleDateString()}</p>
                        </div>

                        {/* Tabla de clientes */}
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border p-2 text-left">Cliente</th>
                                    {/* <th className="border p-2 text-left">Datos de Contacto</th>
                                    <th className="border p-2 text-left">Suscripción</th> */}
                                    <th className="border p-2 text-left">Pagos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClients.map(client => (
                                    <tr key={client._id} className="border-b">
                                        <td className="border p-2 align-top">
                                            <div className="font-bold">{client.nombre}</div>
                                            <div className="text-sm">DNI: {client.dni}</div>
                                            {client.observaciones && (
                                                <div className="text-sm mt-1">
                                                    <span className="font-semibold">Notas:</span> {client.observaciones}
                                                </div>
                                            )}
                                        </td>
                                        {/* <td className="border p-2 align-top">
                                            <div>Tel: {client.telefono}</div>
                                            <div className="text-sm">{client.email}</div>
                                            {client.observaciones && (
                                                <div className="text-sm mt-1">
                                                    <span className="font-semibold">Notas:</span> {client.observaciones}
                                                </div>
                                            )}
                                        </td>
                                        <td className="border p-2 align-top">
                                            {client.suscripciones.map(suscripcion => (
                                                <div key={suscripcion._id} className="mb-2">
                                                    <div>
                                                        <span className="font-semibold">Importe:</span> {formatNumber(suscripcion.importeSuscripcion)}€
                                                    </div>
                                                    {suscripcion.descuento > 0 && (
                                                        <div>
                                                            <span className="font-semibold">Descuento:</span> {formatNumber(suscripcion.descuento)}€
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="font-semibold">Total:</span> {formatNumber(suscripcion.importeSuscripcion - suscripcion.descuento)}€
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold">Método:</span> {suscripcion.tipoPago}
                                                    </div>
                                                    <div className="text-sm">
                                                        <span className="font-semibold">Alta:</span> {formatDate(suscripcion.fechaAltaContrato)}
                                                    </div>
                                                    {suscripcion.observaciones && (
                                                        <div className="text-sm mt-1">
                                                            <span className="font-semibold">Notas:</span> {suscripcion.observaciones}
                                                        </div>
                                                    )}
                                                    {client.plazas && client.plazas.length > 0 && (
                                                        <div className="mt-1">
                                                            <span className="font-semibold">Plazas:</span> {client.plazas.length}
                                                            <ul className="text-xs pl-4">
                                                                {client.plazas.map((plaza, idx) => (
                                                                    <li key={idx}>
                                                                        {plaza.dia} {plaza.horaInicio} - {typeof plaza.nivel_id === 'object' 
                                                                            ? plaza.nivel_id.nombre 
                                                                            : (typeof plaza.nivel === 'object' ? plaza.nivel.nombre : 'Nivel no especificado')}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </td> */}
                                        <td className="border p-2 align-top">
                                            {client.suscripciones.map(suscripcion => {
                                                const pagos = filterPayments(suscripcion.pagos || []);
                                                return pagos.length > 0 ? (
                                                    <div key={suscripcion._id}>
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="bg-gray-50">
                                                                    <th className="p-1 text-left">Fecha</th>
                                                                    <th className="p-1 text-left">Mes/Año</th>
                                                                    <th className="p-1 text-left">Importe</th>
                                                                    <th className="p-1 text-left">Estado</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {pagos.map(pago => (
                                                                    <tr key={pago._id} className={pago.estado === 'Abierto' ? 'bg-red-50' : 'bg-green-50'}>
                                                                        <td className="p-1">
                                                                            {pago.fechaPago ? formatDate(pago.fechaPago) : '-'}
                                                                        </td>
                                                                        <td className="p-1">
                                                                            {pago.mes}/{pago.anio}
                                                                        </td>
                                                                        <td className="p-1">
                                                                            {formatNumber(pago.importe)}€
                                                                        </td>
                                                                        <td className="p-1">
                                                                            <span className={pago.estado === 'Abierto' 
                                                                                ? 'text-red-600 font-semibold' 
                                                                                : 'text-green-600 font-semibold'}>
                                                                                {pago.estado}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div key={suscripcion._id} className="text-gray-500 italic">
                                                        No hay pagos registrados
                                                    </div>
                                                );
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Resumen financiero */}
                        <div className="mt-6 border-t pt-4">
                            <h3 className="text-lg font-bold mb-2">Resumen Financiero</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="border p-3 rounded bg-red-50">
                                    <p className="font-bold">Total Pendiente:</p>
                                    <p className="text-xl text-red-600">{formatNumber(totals.totalAbierto)}€</p>
                                </div>
                                <div className="border p-3 rounded bg-green-50">
                                    <p className="font-bold">Total Cobrado:</p>
                                    <p className="text-xl text-green-600">{formatNumber(totals.totalCerrado)}€</p>
                                </div>
                                <div className="border p-3 rounded bg-blue-50">
                                    <p className="font-bold">Total General:</p>
                                    <p className="text-xl text-blue-600">{formatNumber(totals.totalAbierto + totals.totalCerrado)}€</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <Button onClick={() => handlePrint()}>
                        <Printer size={18} />
                        Imprimir Listado
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default OtherPaymentsPrintList;